import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { LocationOption, TerminalOption } from 'Src/feature/provision/provisionService';
import { setLSToken } from 'Src/feature/terminal/terminalService';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { logOut } from 'Src/network/socket';
import useRoute from 'Src/routes/useRoute';

import { ProvisionItemCta } from './ProvisionItemCTA';
import { useProvisionerTerminal } from './useProvisionerTerminal';
import WaitSplash from './WaitSplash';

import styles from './ClaimTerminal.module.scss';

interface ClaimTerminalProps {
  location?: Omit<LocationOption, 'terminals'>;
  terminal?: Omit<TerminalOption, 'isProvisioned'>;
  /**
   * domEventName is the event the component should listen for to know that
   * it should finalize the claim
   */
  domEventName: keyof HTMLElementEventMap;
}

const ClaimTerminal: FC<ClaimTerminalProps> = ({
  location,
  terminal,
  domEventName,
}) => {
  const log = useLogger().getSubLogger({ name: 'ClaimTerminal' });
  const { t } = useTranslation();
  const { crumbs, navigate } = useRoute();

  const provisionTerminal = useProvisionerTerminal.mutate.provisionTerminal();
  const terminalLocation = useTerminal.query.get();

  const [isClaiming, isClaimingSet] = useState<boolean>(false);

  const finalizeClaim = async () => {
    isClaimingSet(true);
    try {
      const { token } = await provisionTerminal.mutateAsync({ terminalId: terminal?.id as number });
      setLSToken(token);
      // here's the sneaky part... the current session is authenticated as a provisioner...
      // So, we need to kill that, retrieve the POS terminal via the token, and then route back to /pos
      await logOut();
      await terminalLocation.refetch({ throwOnError: true });
      navigate(crumbs.pos.main.path);
    } catch (err) {
      log.warn('Provisioner ClaimTerminal error: ', err);
    } finally {
      isClaimingSet(false);
    }
  };

  useDOMEventListener(domEventName, (evt: Event) => {
    evt.preventDefault();
    void finalizeClaim();
  });

  return (
    <Flex alignItems="center" justifyContent="center" gap="sm" fullHeight fullWidth>
      {isClaiming && <WaitSplash message={t('provision.terminalClaimed.info.claiming')} />}
      <Copy variant="body" weight="bold" align="center">
        {t('provision.terminalClaimed.info.assignedTo')}
      </Copy>
      <ProvisionItemCta
        className={styles.entity}
        selected
        title={location?.name}
        subTitle={t('labels.fullAddress', location?.address)}
      />
      <Copy variant="body" weight="bold" align="center">
        {t('provision.terminalClaimed.info.knownAs')}
      </Copy>
      <ProvisionItemCta
        className={styles.entity}
        selected
        title={terminal?.name}
        subTitle={terminal?.role === 'POS' ? t('labels.pos') : t('labels.timeClock')}
      />
      <Copy className={styles.notice} variant="h6" align="center" color="secondary">
        {t('provision.terminalClaimed.info.claimedUponClicking')}
      </Copy>
    </Flex>
  );
};

export default ClaimTerminal;
